import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import type { Dispatch, SetStateAction } from 'react'
import type { GridApi } from 'ag-grid-community'
import type { AgGridReactProps } from 'ag-grid-react'
import { v4 as uuidv4 } from 'uuid'

import { useUserContext } from '@/context'
import { useSessionRuntime } from '@/context/SessionRuntimeContext'
import { useLatestRef } from '@/hooks/useLatestRef'
import {
  type GridViewInput,
  type GridViewPreferenceInput,
  GridViewWriteError,
} from '@/services/mutations/gridView'
import {
  useAuthorizedCreateGridView,
  useAuthorizedDeleteGridView,
  useAuthorizedUpdateGridView,
  useAuthorizedUpsertGridViewPreference,
} from '@/features/permissions/gridView.mutations'
import {
  useGridView,
  useGridViewPreference,
  useOrganizationGridViews,
} from '@/services/queries/gridView'
import type {
  GridViewItem,
  GridViewPreferenceItem,
  GridViewSelectedRef,
} from '@/services/gridView/gridView.types'
import {
  buildExplicitSystemGridViewRef,
  buildGridViewPreferenceId,
  buildInactivePersonalGridViewRef,
  buildSystemGridViewRef,
  buildTeamGridViewRef,
  DEFAULT_GRID_VIEW_REF,
  parseGridViewSelectedRef,
  PERSONAL_GRID_VIEW_REF,
} from '@/services/gridView/gridView.types'

import type {
  ColumnStateConfig,
  ExternalFilters,
  ExternalFiltersConfig,
  GridScope,
  GridViewChangeCapture,
  GridViewPayload,
  GridViewsConfig,
  GridViewsContextValue,
} from '../types'
import {
  applyGridViewPayload,
  areGridViewPayloadsEqual,
  buildGridViewInitialState,
  buildSystemGridViewPayload,
  captureGridViewPayload,
  getGridViewPayloadVersion,
  isCompatibleGridViewPayloadVersion,
  parseGridViewPayload,
  reconcileGridViewPayload,
  serializeGridViewPayload,
  validateGridViewPayloadForApi,
} from './payload'
import { resolveGridViewPermissions } from './permissions'
import { resolveEffectiveGridViewState } from './resolution'
import { isUserGridViewEvent } from './events'
import { resolveSystemGridViewPresets } from './systemPresets'
import {
  getGridViewPreferenceCoordinator,
  GRID_VIEW_PREFERENCE_DEBOUNCE_MS,
  type GridViewPreferenceCoordinator,
  type GridViewPreferenceSnapshot,
  type GridViewPreferenceWriter,
} from '@/services/gridView/gridViewPreferenceCoordinator'

type UseGridViewsManagerParams<Row, Ctx> = {
  config?: GridViewsConfig
  scope: GridScope
  api: GridApi<Row> | null
  gridIsReady: boolean
  columnDefsReady: boolean
  columnDefs: AgGridReactProps<Row>['columnDefs']
  columnStateConfig?: ColumnStateConfig
  externalFiltersConfig?: ExternalFiltersConfig<Row, Ctx>
  externalFiltersSelected: ExternalFilters
  setExternalFiltersSelected: Dispatch<SetStateAction<ExternalFilters>>
}

type PreferenceSnapshot = Pick<
  GridViewPreferenceItem,
  | 'id'
  | 'scopeKey'
  | 'selectedViewRef'
  | 'draftPayload'
  | 'payloadVersion'
  | 'gridSchemaVersion'
> & {
  revision?: number | null
}

type SelectedSharedViewDetail = {
  scopeKey: string | null
  view: GridViewItem
}

type HydratedSharedViewsData = {
  scopeKey: string | null
  dataUpdatedAt?: number
  data: unknown
}

const isSameHydratedSharedViewsData = (
  left: HydratedSharedViewsData | null | undefined,
  right: HydratedSharedViewsData,
) => {
  if (!left || left.scopeKey !== right.scopeKey) return false

  return right.dataUpdatedAt !== undefined || left.dataUpdatedAt !== undefined
    ? left.dataUpdatedAt === right.dataUpdatedAt
    : left.data === right.data
}

const EMPTY_SHARED_VIEWS: GridViewItem[] = []

const sortViewsByUpdatedAtDesc = (left: GridViewItem, right: GridViewItem) =>
  (right.updatedAt ?? '').localeCompare(left.updatedAt ?? '')

const matchesActiveGridViewScope = (
  view: GridViewItem | null | undefined,
  viewId: string | undefined,
  organizationID: string | undefined,
  scopeKey: string | undefined,
) =>
  Boolean(viewId) &&
  view != null &&
  view.id === viewId &&
  view.organizationID === organizationID &&
  view.scopeKey === scopeKey

const mergeSharedViews = (...groups: readonly GridViewItem[][]) => {
  const viewsById = new Map<string, GridViewItem>()

  groups.forEach((group) => {
    group.forEach((view) => {
      if (!viewsById.has(view.id)) viewsById.set(view.id, view)
    })
  })

  return [...viewsById.values()].sort(sortViewsByUpdatedAtDesc)
}

const isSamePreferenceSnapshot = (
  left: PreferenceSnapshot | null | undefined,
  right: PreferenceSnapshot,
) =>
  left?.id === right.id &&
  left?.scopeKey === right.scopeKey &&
  (left?.selectedViewRef ?? null) === (right.selectedViewRef ?? null) &&
  normalizePreferenceDraftPayload(left?.draftPayload) ===
    normalizePreferenceDraftPayload(right.draftPayload) &&
  left?.payloadVersion === right.payloadVersion &&
  left?.gridSchemaVersion === right.gridSchemaVersion &&
  (right.revision === undefined ||
    (left?.revision ?? 0) === (right.revision ?? 0))

const normalizePreferenceDraftPayload = (payload: string | null | undefined) =>
  payload ?? null

const isSameSharedView = (left: GridViewItem, right: GridViewItem) =>
  left.id === right.id &&
  left.organizationID === right.organizationID &&
  left.scopeKey === right.scopeKey &&
  left.name === right.name &&
  (left.description ?? null) === (right.description ?? null) &&
  left.payload === right.payload &&
  left.payloadVersion === right.payloadVersion &&
  left.gridSchemaVersion === right.gridSchemaVersion &&
  left.status === right.status &&
  left.isDefault === right.isDefault &&
  left.createdByUserID === right.createdByUserID &&
  (left.updatedByUserID ?? null) === (right.updatedByUserID ?? null) &&
  (left.createdAt ?? null) === (right.createdAt ?? null) &&
  (left.updatedAt ?? null) === (right.updatedAt ?? null) &&
  (left.source ?? null) === (right.source ?? null)

const isSameSharedViewsState = (
  left: readonly GridViewItem[],
  right: readonly GridViewItem[],
) =>
  left.length === right.length &&
  left.every((leftView, index) => {
    const rightView = right[index]
    return Boolean(rightView && isSameSharedView(leftView, rightView))
  })

const isSameSelectedSharedViewDetail = (
  left: SelectedSharedViewDetail | null,
  right: SelectedSharedViewDetail | null,
) => {
  if (!left || !right) return left === right

  return (
    left.scopeKey === right.scopeKey && isSameSharedView(left.view, right.view)
  )
}

const getNextTimestamp = () => new Date().toISOString()

const buildPendingPreferenceItem = ({
  snapshot,
  userId,
  currentPreference,
}: {
  snapshot: GridViewPreferenceSnapshot
  userId: string
  currentPreference?: GridViewPreferenceItem | null
}): GridViewPreferenceItem => ({
  id: snapshot.preferenceId,
  userID: userId,
  organizationID: snapshot.organizationID,
  scopeKey: snapshot.scopeKey,
  selectedViewRef: snapshot.selectedViewRef,
  draftPayload: snapshot.draftPayload,
  payloadVersion: snapshot.payloadVersion,
  gridSchemaVersion: snapshot.gridSchemaVersion,
  status: 'active',
  createdAt: currentPreference?.createdAt ?? getNextTimestamp(),
  updatedAt: currentPreference?.updatedAt ?? getNextTimestamp(),
  revision: snapshot.expectedRevision,
  source: 'manage',
})

const hasColumnSizing = (payload: GridViewPayload | null | undefined) =>
  (payload?.columnSizing?.columns.length ?? 0) > 0

const parkPersonalDraftOnSelectedRef = ({
  selectedViewRef,
  draftPayload,
}: {
  selectedViewRef: GridViewSelectedRef
  draftPayload?: string | null
}): GridViewSelectedRef =>
  draftPayload
    ? buildInactivePersonalGridViewRef(selectedViewRef)
    : selectedViewRef

const selectedRefHasInactivePersonalDraft = (
  selectedViewRef: string | null | undefined,
) => {
  const selectedRef = parseGridViewSelectedRef(selectedViewRef)
  return (
    'isPersonalDraftInactive' in selectedRef &&
    selectedRef.isPersonalDraftInactive
  )
}

const selectedRefCanCaptureActivePersonalDraft = (
  selectedViewRef: string | null | undefined,
) => {
  const selectedRef = parseGridViewSelectedRef(selectedViewRef)
  if (
    'isPersonalDraftInactive' in selectedRef &&
    selectedRef.isPersonalDraftInactive
  ) {
    return false
  }

  return true
}

export const useGridViewsManager = <Row, Ctx>({
  config,
  scope,
  api,
  gridIsReady,
  columnDefsReady,
  columnDefs,
  columnStateConfig,
  externalFiltersConfig,
  externalFiltersSelected,
  setExternalFiltersSelected,
}: UseGridViewsManagerParams<Row, Ctx>): GridViewsContextValue | null => {
  const { user, isGuest, isAdmin, organizationScope } = useUserContext()
  const { isImpersonating } = useSessionRuntime()
  const enabled = Boolean(config)
  const activeOrganizationId =
    enabled && organizationScope.scopeKind === 'organization'
      ? organizationScope.activeOrganizationId
      : undefined
  const userId = user?.id
  const canonicalScopeKey = config?.scopeKey || undefined
  const preferenceId =
    activeOrganizationId && userId && canonicalScopeKey
      ? buildGridViewPreferenceId({
          userID: userId,
          organizationID: activeOrganizationId,
          scopeKey: canonicalScopeKey,
        })
      : undefined

  const basePermissions = resolveGridViewPermissions({
    activeOrganizationId,
    scopeKind: organizationScope.scopeKind,
    userId,
    isGuest,
    isAdmin,
  })

  const sharedViewsQuery = useOrganizationGridViews({
    organizationID: activeOrganizationId,
    scopeKey: canonicalScopeKey,
    enabled: enabled && basePermissions.canReadSharedViews,
  })
  const canonicalPreferenceQuery = useGridViewPreference({
    id: preferenceId,
    enabled: enabled && basePermissions.canReadSharedViews,
  })

  const createGridViewMutation = useAuthorizedCreateGridView()
  const updateGridViewMutation = useAuthorizedUpdateGridView()
  const deleteGridViewMutation = useAuthorizedDeleteGridView()
  const upsertPreferenceMutation = useAuthorizedUpsertGridViewPreference()

  const [sharedViewsState, setSharedViewsState] = useState<GridViewItem[]>([])
  const [sharedViewsStateScopeKey, setSharedViewsStateScopeKey] = useState<
    string | null
  >(null)
  const [preferenceState, setPreferenceState] =
    useState<GridViewPreferenceItem | null>(null)
  const [inactivePersonalDraftPayload, setInactivePersonalDraftPayload] =
    useState<string | null>(null)
  const [hasPendingUserChange, setHasPendingUserChange] = useState(false)
  const [selectedSharedViewDetail, setSelectedSharedViewDetail] =
    useState<SelectedSharedViewDetail | null>(null)
  const selectedSharedViewDetailRef = useLatestRef(selectedSharedViewDetail)
  const lastHydratedSharedViewsDataRef = useRef<HydratedSharedViewsData | null>(
    null,
  )
  const draftSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const viewStateScopeKey =
    preferenceId && config?.gridSchemaVersion
      ? `${preferenceId}:${config.gridSchemaVersion}`
      : null
  const viewStateScopeEpochRef = useRef({
    key: viewStateScopeKey,
    epoch: 0,
  })
  if (viewStateScopeEpochRef.current.key !== viewStateScopeKey) {
    viewStateScopeEpochRef.current = {
      key: viewStateScopeKey,
      epoch: viewStateScopeEpochRef.current.epoch + 1,
    }
  }
  const viewStateScopeEpoch = viewStateScopeEpochRef.current.epoch
  const [localViewStateScopeKey, setLocalViewStateScopeKey] = useState<
    string | null
  >(viewStateScopeKey)
  const isLocalViewStateScoped = localViewStateScopeKey === viewStateScopeKey
  const scopedSharedViewsState = isLocalViewStateScoped
    ? sharedViewsState
    : EMPTY_SHARED_VIEWS
  const scopedPreferenceState = isLocalViewStateScoped ? preferenceState : null
  const scopedInactivePersonalDraftPayload = isLocalViewStateScoped
    ? inactivePersonalDraftPayload
    : null
  const viewStateScopeKeyRef = useLatestRef(viewStateScopeKey)

  const preferenceCoordinatorKey =
    !isImpersonating &&
    preferenceId &&
    activeOrganizationId &&
    canonicalScopeKey &&
    config?.gridSchemaVersion &&
    userId
      ? [
          'grid-view-preference',
          preferenceId,
          activeOrganizationId,
          canonicalScopeKey,
          config.gridSchemaVersion,
        ].join(':')
      : null
  const preferenceWriteBlockedRef = useRef(isImpersonating)
  const preferenceWriter = useCallback(
    async (snapshot: Parameters<GridViewPreferenceWriter>[0]) => {
      if (preferenceWriteBlockedRef.current) {
        throw new GridViewWriteError('Unauthorized')
      }
      const result = await upsertPreferenceMutation.mutateAsync({
        upsertType: snapshot.expectedRevision == null ? 'create' : 'update',
        input: {
          id: snapshot.preferenceId,
          userID: userId!,
          organizationID: snapshot.organizationID,
          scopeKey: snapshot.scopeKey,
          selectedViewRef: snapshot.selectedViewRef,
          draftPayload: snapshot.draftPayload,
          payloadVersion: snapshot.payloadVersion,
          gridSchemaVersion: snapshot.gridSchemaVersion,
          expectedRevision: snapshot.expectedRevision,
        },
      })
      return { status: result.status, revision: result.revision }
    },
    [upsertPreferenceMutation.mutateAsync, userId],
  )
  const preferenceWriterRef = useLatestRef(preferenceWriter)
  const [preferenceCoordinatorEntry, setPreferenceCoordinatorEntry] = useState<{
    key: string
    coordinator: GridViewPreferenceCoordinator
  } | null>(null)
  const [
    hydratedPreferenceCoordinatorKey,
    setHydratedPreferenceCoordinatorKey,
  ] = useState<string | null>(null)

  useEffect(() => {
    preferenceCoordinatorEntry?.coordinator.setWriter(preferenceWriter)
  }, [preferenceCoordinatorEntry, preferenceWriter])

  const preferenceCoordinator =
    preferenceCoordinatorEntry?.key === preferenceCoordinatorKey
      ? preferenceCoordinatorEntry.coordinator
      : null

  const subscribeToPreferenceCoordinator = useCallback(
    (listener: () => void) =>
      preferenceCoordinator?.subscribe(listener) ?? (() => {}),
    [preferenceCoordinator],
  )
  const getPreferenceCoordinatorState = useCallback(
    () => preferenceCoordinator?.getState() ?? 'idle',
    [preferenceCoordinator],
  )
  const preferenceWriteState = useSyncExternalStore(
    subscribeToPreferenceCoordinator,
    getPreferenceCoordinatorState,
    getPreferenceCoordinatorState,
  )

  const preferenceData = canonicalPreferenceQuery.data ?? null
  const canonicalSharedViews = useMemo(() => {
    const viewsById = new Map<string, GridViewItem>()

    for (const view of sharedViewsQuery.data ?? []) {
      if (
        !matchesActiveGridViewScope(
          view,
          view.id,
          activeOrganizationId,
          canonicalScopeKey,
        ) ||
        viewsById.has(view.id)
      ) {
        continue
      }

      viewsById.set(view.id, view)
    }

    return [...viewsById.values()].sort(sortViewsByUpdatedAtDesc)
  }, [activeOrganizationId, canonicalScopeKey, sharedViewsQuery.data])
  const selectedSharedViewRef = parseGridViewSelectedRef(
    scopedPreferenceState?.selectedViewRef,
  )
  const selectedSharedViewId =
    selectedSharedViewRef.type === 'team' ? selectedSharedViewRef.id : undefined
  const selectedSharedViewFromList = selectedSharedViewId
    ? (canonicalSharedViews.find((view) => view.id === selectedSharedViewId) ??
      null)
    : null
  const selectedSharedViewDetailCandidate = selectedSharedViewId
  const selectedSharedViewIsInServerList = canonicalSharedViews.some(
    (view) => view.id === selectedSharedViewDetailCandidate,
  )
  const shouldFetchSelectedSharedView = Boolean(
    selectedSharedViewDetailCandidate && !selectedSharedViewIsInServerList,
  )
  const selectedSharedViewQuery = useGridView({
    id: selectedSharedViewDetailCandidate,
    enabled:
      enabled &&
      basePermissions.canReadSharedViews &&
      shouldFetchSelectedSharedView,
  })
  const selectedSharedViewFromDetail =
    selectedSharedViewQuery.data &&
    selectedSharedViewQuery.data.id === selectedSharedViewDetailCandidate &&
    selectedSharedViewQuery.data.organizationID === activeOrganizationId &&
    selectedSharedViewQuery.data.scopeKey === canonicalScopeKey
      ? selectedSharedViewQuery.data
      : null
  const selectedSharedViewFallback =
    selectedSharedViewFromDetail &&
    !scopedSharedViewsState.some(
      (view) => view.id === selectedSharedViewFromDetail.id,
    )
      ? selectedSharedViewFromDetail
      : null
  const isSharedViewsStateInitialized =
    viewStateScopeKey !== null && sharedViewsStateScopeKey === viewStateScopeKey
  const effectiveSharedViewsState = useMemo(() => {
    const baseSharedViewsState = isSharedViewsStateInitialized
      ? scopedSharedViewsState
      : canonicalSharedViews

    if (!selectedSharedViewFallback) return baseSharedViewsState
    if (
      baseSharedViewsState.some(
        (view) => view.id === selectedSharedViewFallback.id,
      )
    ) {
      return baseSharedViewsState
    }

    return [...baseSharedViewsState, selectedSharedViewFallback].sort(
      sortViewsByUpdatedAtDesc,
    )
  }, [
    canonicalSharedViews,
    scopedSharedViewsState,
    selectedSharedViewFallback,
    isSharedViewsStateInitialized,
  ])

  const sharedViewsLoading = Boolean(
    sharedViewsQuery.isLoading || sharedViewsQuery.isFetching,
  )
  const preferencesLoading = Boolean(
    canonicalPreferenceQuery.isLoading || canonicalPreferenceQuery.isFetching,
  )
  const selectedSharedViewLoading =
    shouldFetchSelectedSharedView &&
    Boolean(
      selectedSharedViewQuery.isLoading || selectedSharedViewQuery.isFetching,
    )
  const selectedSharedViewDetailCanBeEvicted =
    shouldFetchSelectedSharedView &&
    selectedSharedViewQuery.isFetchedAfterMount &&
    selectedSharedViewQuery.isSuccess &&
    selectedSharedViewQuery.fetchStatus === 'idle' &&
    !selectedSharedViewQuery.isPlaceholderData &&
    !selectedSharedViewLoading &&
    !selectedSharedViewQuery.isError
  const serverQueriesError = Boolean(
    sharedViewsQuery.isError ||
    canonicalPreferenceQuery.isError ||
    (shouldFetchSelectedSharedView && selectedSharedViewQuery.isError),
  )
  const serverQueriesReady =
    !basePermissions.canReadSharedViews ||
    (!sharedViewsLoading && !preferencesLoading && !selectedSharedViewLoading)
  const canHydrateServerState = serverQueriesReady && !serverQueriesError

  // Keep queued drafts in the coordinator when reads fail or support starts.
  preferenceWriteBlockedRef.current = serverQueriesError || isImpersonating

  useEffect(() => {
    if (localViewStateScopeKey === viewStateScopeKey) return

    setLocalViewStateScopeKey(viewStateScopeKey)
    setSharedViewsState([])
    setSharedViewsStateScopeKey(null)
    setPreferenceState(null)
    setInactivePersonalDraftPayload(null)
    setHasPendingUserChange(false)
    setSelectedSharedViewDetail(null)
  }, [localViewStateScopeKey, viewStateScopeKey])

  const canRetainSelectedSharedViewDetail =
    !selectedSharedViewDetailCanBeEvicted &&
    Boolean(selectedSharedViewId) &&
    selectedSharedViewDetail?.scopeKey === viewStateScopeKey &&
    selectedSharedViewDetail.view.id === selectedSharedViewId &&
    matchesActiveGridViewScope(
      selectedSharedViewDetail?.view,
      selectedSharedViewDetail?.view.id,
      activeOrganizationId,
      canonicalScopeKey,
    )
  const retainedSelectedSharedView =
    selectedSharedViewFromDetail ??
    selectedSharedViewFromList ??
    (canRetainSelectedSharedViewDetail
      ? (selectedSharedViewDetail?.view ?? null)
      : null)
  const previousSharedViewsInActiveScope = useMemo(
    () =>
      scopedSharedViewsState.filter((view) =>
        matchesActiveGridViewScope(
          view,
          view.id,
          activeOrganizationId,
          canonicalScopeKey,
        ),
      ),
    [activeOrganizationId, canonicalScopeKey, scopedSharedViewsState],
  )
  const hasUsableSharedViewsData =
    sharedViewsQuery.data !== undefined && !sharedViewsQuery.isError
  const sharedViewsForHydration = useMemo(() => {
    if (hasUsableSharedViewsData) {
      if (canonicalSharedViews.length > 0) return canonicalSharedViews

      if (
        selectedSharedViewDetailCandidate &&
        !selectedSharedViewDetailCanBeEvicted
      ) {
        return previousSharedViewsInActiveScope.filter(
          (view) => view.id === selectedSharedViewDetailCandidate,
        )
      }

      return []
    }

    return previousSharedViewsInActiveScope
  }, [
    canonicalSharedViews,
    hasUsableSharedViewsData,
    previousSharedViewsInActiveScope,
    selectedSharedViewDetailCanBeEvicted,
    selectedSharedViewDetailCandidate,
  ])

  useEffect(() => {
    if (!canHydrateServerState) return

    const currentSharedViewsData: HydratedSharedViewsData = {
      scopeKey: viewStateScopeKey,
      dataUpdatedAt: sharedViewsQuery.dataUpdatedAt,
      data: sharedViewsQuery.data,
    }
    const isNewUsableSharedViewsData =
      hasUsableSharedViewsData &&
      !isSameHydratedSharedViewsData(
        lastHydratedSharedViewsDataRef.current,
        currentSharedViewsData,
      )

    if (sharedViewsQuery.data !== undefined && !sharedViewsQuery.isError) {
      lastHydratedSharedViewsDataRef.current = currentSharedViewsData
    }

    const nextSelectedSharedViewDetail = selectedSharedViewFromDetail
      ? {
          scopeKey: viewStateScopeKey,
          view: selectedSharedViewFromDetail,
        }
      : selectedSharedViewFromList
        ? {
            scopeKey: viewStateScopeKey,
            view: selectedSharedViewFromList,
          }
        : canRetainSelectedSharedViewDetail
          ? selectedSharedViewDetail
          : null

    setSelectedSharedViewDetail((currentDetail) =>
      isSameSelectedSharedViewDetail(
        currentDetail,
        nextSelectedSharedViewDetail,
      )
        ? currentDetail
        : nextSelectedSharedViewDetail,
    )

    const nextSharedViews = mergeSharedViews(
      isNewUsableSharedViewsData && !selectedSharedViewDetailCandidate
        ? canonicalSharedViews
        : sharedViewsForHydration,
      retainedSelectedSharedView ? [retainedSelectedSharedView] : [],
    )
    setSharedViewsStateScopeKey(viewStateScopeKey)
    setSharedViewsState((currentViews) =>
      isSameSharedViewsState(currentViews, nextSharedViews)
        ? currentViews
        : nextSharedViews,
    )
  }, [
    canHydrateServerState,
    canRetainSelectedSharedViewDetail,
    selectedSharedViewDetailCanBeEvicted,
    selectedSharedViewDetail,
    selectedSharedViewFromDetail,
    selectedSharedViewFromList,
    sharedViewsForHydration,
    retainedSelectedSharedView,
    selectedSharedViewId,
    canonicalSharedViews,
    hasUsableSharedViewsData,
    sharedViewsQuery.data,
    sharedViewsQuery.dataUpdatedAt,
    sharedViewsQuery.isError,
    viewStateScopeKey,
  ])

  useEffect(() => {
    if (!canHydrateServerState) return

    setPreferenceState((currentPreference) => {
      const pendingSnapshot = preferenceCoordinator?.getPendingSnapshot()
      const acknowledgedSnapshot =
        preferenceCoordinator?.getLastAcknowledgedSnapshot()
      const localSnapshot = pendingSnapshot ?? acknowledgedSnapshot
      const remoteRevision = preferenceData?.revision ?? 0
      if (
        currentPreference &&
        typeof currentPreference.revision === 'number' &&
        currentPreference.revision > remoteRevision
      ) {
        return currentPreference
      }
      const localRevision = localSnapshot?.expectedRevision
      const shouldKeepLocalSnapshot =
        Boolean(pendingSnapshot) ||
        (Boolean(acknowledgedSnapshot) &&
          typeof localRevision === 'number' &&
          remoteRevision <= localRevision)

      if (shouldKeepLocalSnapshot && localSnapshot && userId) {
        const localPreference = buildPendingPreferenceItem({
          snapshot: localSnapshot,
          userId,
          currentPreference,
        })

        return isSamePreferenceSnapshot(currentPreference, localPreference)
          ? currentPreference
          : localPreference
      }

      if (!preferenceData) {
        return currentPreference === null ? currentPreference : null
      }
      return isSamePreferenceSnapshot(currentPreference, preferenceData)
        ? currentPreference
        : preferenceData
    })
  }, [canHydrateServerState, preferenceCoordinator, preferenceData, userId])

  useEffect(() => {
    if (!preferenceCoordinator || !canHydrateServerState) return
    preferenceCoordinator.hydrateRemoteRevision(preferenceData?.revision ?? 0)
    setHydratedPreferenceCoordinatorKey(preferenceCoordinatorKey)
  }, [
    canHydrateServerState,
    preferenceCoordinator,
    preferenceCoordinatorKey,
    preferenceData?.revision,
  ])

  useEffect(() => {
    if (!preferenceCoordinator || !userId) return

    const applyPendingPreference = () => {
      const pendingSnapshot = preferenceCoordinator.getPendingSnapshot()
      const acknowledgedSnapshot =
        preferenceCoordinator.getLastAcknowledgedSnapshot()
      const localSnapshot = pendingSnapshot ?? acknowledgedSnapshot
      if (!localSnapshot) {
        return
      }

      if (
        localSnapshot.preferenceId !== preferenceId ||
        localSnapshot.organizationID !== activeOrganizationId ||
        localSnapshot.scopeKey !== canonicalScopeKey ||
        localSnapshot.gridSchemaVersion !== config?.gridSchemaVersion
      ) {
        return
      }

      if (
        !pendingSnapshot &&
        typeof localSnapshot.expectedRevision === 'number' &&
        (preferenceData?.revision ?? 0) >= localSnapshot.expectedRevision
      ) {
        return
      }

      setPreferenceState((currentPreference) => {
        const pendingPreference = buildPendingPreferenceItem({
          snapshot: localSnapshot,
          userId,
          currentPreference,
        })

        return isSamePreferenceSnapshot(currentPreference, pendingPreference)
          ? currentPreference
          : pendingPreference
      })
    }

    applyPendingPreference()
    return preferenceCoordinator.subscribe(applyPendingPreference)
  }, [
    activeOrganizationId,
    canHydrateServerState,
    canonicalScopeKey,
    config?.gridSchemaVersion,
    preferenceData,
    preferenceCoordinator,
    preferenceId,
    preferenceData?.revision,
    userId,
  ])

  const preferenceRef = useLatestRef(scopedPreferenceState)
  const sharedViewsRef = useLatestRef(effectiveSharedViewsState)
  const externalFiltersSelectedRef = useLatestRef(externalFiltersSelected)
  const remotePreferenceApplyRef = useRef<
    GridViewPreferenceItem | null | undefined
  >(undefined)
  const conflictResolutionPromiseRef = useRef<Promise<void> | null>(null)
  const [isApplyingRemotePreference, setIsApplyingRemotePreference] =
    useState(false)

  useEffect(() => {
    remotePreferenceApplyRef.current = undefined
    conflictResolutionPromiseRef.current = null
    setIsApplyingRemotePreference(false)
  }, [viewStateScopeEpoch])

  const canPerformViewAction = useCallback(
    () =>
      !preferenceWriteBlockedRef.current &&
      !serverQueriesError &&
      !isImpersonating &&
      !preferenceCoordinator?.getConflict() &&
      !isApplyingRemotePreference,
    [
      isApplyingRemotePreference,
      isImpersonating,
      preferenceCoordinator,
      serverQueriesError,
    ],
  )
  const assertViewActionAllowed = useCallback(() => {
    if (!canPerformViewAction()) {
      throw new GridViewWriteError(
        serverQueriesError || isImpersonating ? 'Unauthorized' : 'Conflict',
      )
    }
  }, [canPerformViewAction, isImpersonating, serverQueriesError])

  const permissionsForView = useCallback(
    (view: GridViewItem | null | undefined) =>
      resolveGridViewPermissions({
        activeOrganizationId,
        scopeKind: organizationScope.scopeKind,
        userId,
        isGuest,
        isAdmin,
        createdByUserID: view?.createdByUserID,
      }),
    [
      activeOrganizationId,
      isAdmin,
      isGuest,
      organizationScope.scopeKind,
      userId,
    ],
  )

  const systemPayload = useMemo(() => {
    if (!config) return null

    return buildSystemGridViewPayload({
      columnDefs,
      defaultExternalFilters: externalFiltersConfig?.defaultSelected,
      contextualExternalFilterFields: config.contextualExternalFilterFields,
      ignoreColumn: columnStateConfig?.ignoreColumn,
    })
  }, [
    columnDefs,
    columnStateConfig?.ignoreColumn,
    config,
    externalFiltersConfig?.defaultSelected,
  ])

  const externalFilterFields = Object.keys(externalFiltersConfig?.fields ?? {})

  const gridSchemaVersion = config?.gridSchemaVersion
  const presetContext = config?.presetContext

  const systemViews = useMemo(() => {
    if (!gridSchemaVersion || !canonicalScopeKey || !systemPayload) return []

    return resolveSystemGridViewPresets({
      scopeKey: canonicalScopeKey,
      gridSchemaVersion,
      presetContext: {
        surface: scope.view,
        ...presetContext,
      },
      basePayload: systemPayload,
    })
  }, [
    canonicalScopeKey,
    gridSchemaVersion,
    presetContext,
    scope.view,
    systemPayload,
  ])

  const resolvedState = useMemo(() => {
    if (!enabled || !config || !systemPayload) return null

    return resolveEffectiveGridViewState({
      availableViews: effectiveSharedViewsState,
      preference: scopedPreferenceState,
      systemPayload,
      systemViews,
      preferDefinitionFallback: config.preferDefinitionFallback,
      currentGridSchemaVersion: config.gridSchemaVersion,
      contextualExternalFilterFields: config.contextualExternalFilterFields,
      externalFilterFields,
    })
  }, [
    config,
    enabled,
    effectiveSharedViewsState,
    scopedPreferenceState,
    systemPayload,
    systemViews,
    externalFilterFields,
  ])

  const resolvedStateRef = useLatestRef(resolvedState)
  const systemPayloadRef = useLatestRef(systemPayload)
  const applyInProgressRef = useRef(false)
  const applyReleaseRafRef = useRef<number | null>(null)
  const applyReleaseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const lastAppliedPayloadRef = useRef<GridViewPayload | null>(null)
  const columnSizingDirtyRef = useRef(false)
  const userDraftSyncPendingRef = useRef(false)
  const draftSyncRetryCountRef = useRef(0)
  const retryDraftSyncRef = useRef<(() => void) | null>(null)
  const activeSelectedViewDraftPayloadRef = useRef<string | null>(null)

  const buildPreferenceInput = useCallback(
    ({
      selectedViewRef,
      draftPayload,
    }: {
      selectedViewRef?: string | null
      draftPayload?: string | null
    }): GridViewPreferenceInput | null => {
      if (
        !config ||
        !preferenceId ||
        !activeOrganizationId ||
        !userId ||
        !canonicalScopeKey
      ) {
        return null
      }

      const pendingSnapshot = preferenceCoordinator?.getPendingSnapshot()
      const isPendingCreate =
        pendingSnapshot?.preferenceId === preferenceId &&
        pendingSnapshot.organizationID === activeOrganizationId &&
        pendingSnapshot.scopeKey === canonicalScopeKey &&
        pendingSnapshot.gridSchemaVersion === config.gridSchemaVersion &&
        pendingSnapshot.expectedRevision === null

      return {
        id: preferenceId,
        userID: userId,
        organizationID: activeOrganizationId,
        scopeKey: canonicalScopeKey,
        selectedViewRef: selectedViewRef ?? DEFAULT_GRID_VIEW_REF,
        draftPayload: draftPayload ?? null,
        payloadVersion: getGridViewPayloadVersion(),
        gridSchemaVersion: config.gridSchemaVersion,
        expectedRevision: isPendingCreate
          ? null
          : preferenceRef.current
            ? (preferenceRef.current.revision ?? 0)
            : null,
      }
    },
    [
      activeOrganizationId,
      canonicalScopeKey,
      config,
      preferenceCoordinator,
      preferenceId,
      preferenceRef,
      userId,
    ],
  )

  const persistPreference = useCallback(
    async ({
      selectedViewRef,
      draftPayload,
      force,
      flush = true,
    }: {
      selectedViewRef?: string | null
      draftPayload?: string | null
      force?: boolean
      flush?: boolean
    }) => {
      if (!canPerformViewAction()) return null

      const nextInput = buildPreferenceInput({
        selectedViewRef,
        draftPayload,
      })
      if (!nextInput) return null

      if (
        !viewStateScopeKey ||
        viewStateScopeKeyRef.current !== viewStateScopeKey
      ) {
        return null
      }

      const previousPreference = preferenceRef.current
      if (
        !force &&
        !previousPreference &&
        nextInput.selectedViewRef === DEFAULT_GRID_VIEW_REF &&
        !nextInput.draftPayload
      ) {
        return null
      }
      if (isSamePreferenceSnapshot(previousPreference, nextInput)) {
        return previousPreference
      }

      const optimisticPreference: GridViewPreferenceItem = {
        ...nextInput,
        status: 'active',
        createdAt: previousPreference?.createdAt ?? getNextTimestamp(),
        updatedAt: getNextTimestamp(),
        revision: previousPreference
          ? (previousPreference.revision ?? 0)
          : null,
        source: 'manage',
      }

      setPreferenceState(optimisticPreference)
      preferenceCoordinator?.enqueue({
        preferenceId: nextInput.id,
        organizationID: nextInput.organizationID,
        scopeKey: nextInput.scopeKey,
        gridSchemaVersion: nextInput.gridSchemaVersion,
        payloadVersion: nextInput.payloadVersion,
        selectedViewRef: nextInput.selectedViewRef ?? null,
        draftPayload: nextInput.draftPayload ?? null,
        expectedRevision: nextInput.expectedRevision ?? null,
      })
      if (flush) {
        await preferenceCoordinator?.flush()
      }
      return optimisticPreference
    },
    [
      buildPreferenceInput,
      canPerformViewAction,
      preferenceRef,
      preferenceCoordinator,
      viewStateScopeKey,
      viewStateScopeKeyRef,
    ],
  )

  const cancelApplyRelease = useCallback(() => {
    if (
      applyReleaseRafRef.current !== null &&
      typeof window !== 'undefined' &&
      typeof window.cancelAnimationFrame === 'function'
    ) {
      window.cancelAnimationFrame(applyReleaseRafRef.current)
    }
    applyReleaseRafRef.current = null

    if (applyReleaseTimeoutRef.current) {
      clearTimeout(applyReleaseTimeoutRef.current)
      applyReleaseTimeoutRef.current = null
    }
  }, [])

  const releaseApplyLockAfterGridSettles = useCallback(() => {
    cancelApplyRelease()

    if (
      typeof window !== 'undefined' &&
      typeof window.requestAnimationFrame === 'function'
    ) {
      applyReleaseRafRef.current = window.requestAnimationFrame(() => {
        applyReleaseRafRef.current = window.requestAnimationFrame(() => {
          applyReleaseRafRef.current = null
          applyInProgressRef.current = false
        })
      })
      return
    }

    applyReleaseTimeoutRef.current = setTimeout(() => {
      applyReleaseTimeoutRef.current = null
      applyInProgressRef.current = false
    }, 0)
  }, [cancelApplyRelease])

  const applyResolvedPayload = useCallback(
    (payload = resolvedState?.payload) => {
      if (!api || !payload) return

      applyInProgressRef.current = true
      columnSizingDirtyRef.current = false
      userDraftSyncPendingRef.current = false
      activeSelectedViewDraftPayloadRef.current = null
      lastAppliedPayloadRef.current = payload
      setHasPendingUserChange(false)
      setExternalFiltersSelected(payload.externalFilters)
      applyGridViewPayload({ api, payload })
      releaseApplyLockAfterGridSettles()
    },
    [
      api,
      releaseApplyLockAfterGridSettles,
      resolvedState?.payload,
      setExternalFiltersSelected,
    ],
  )

  useEffect(() => {
    const remotePreference = remotePreferenceApplyRef.current
    if (remotePreference === undefined || !api || !resolvedState) {
      return
    }

    const remoteSelectedRef = parseGridViewSelectedRef(
      remotePreference?.selectedViewRef,
    )
    const remoteTeamViewId =
      remoteSelectedRef.type === 'team' ? remoteSelectedRef.id : null
    const remoteTeamViewIsAvailable = remoteTeamViewId
      ? effectiveSharedViewsState.some((view) => view.id === remoteTeamViewId)
      : true
    const remoteTeamViewIsLoading = Boolean(
      remoteTeamViewId &&
      !remoteTeamViewIsAvailable &&
      (sharedViewsLoading || selectedSharedViewLoading),
    )
    const remoteTeamViewHasError = Boolean(
      remoteTeamViewId &&
      !remoteTeamViewIsAvailable &&
      (sharedViewsQuery.isError ||
        (shouldFetchSelectedSharedView && selectedSharedViewQuery.isError)),
    )
    if (
      preferencesLoading ||
      remoteTeamViewIsLoading ||
      remoteTeamViewHasError
    ) {
      return
    }

    const matchesRemotePreference =
      remotePreference === null
        ? scopedPreferenceState === null
        : isSamePreferenceSnapshot(scopedPreferenceState, remotePreference)
    if (!matchesRemotePreference) return

    applyResolvedPayload(resolvedState.payload)
    remotePreferenceApplyRef.current = undefined
    setIsApplyingRemotePreference(false)
  }, [
    api,
    applyResolvedPayload,
    effectiveSharedViewsState,
    preferencesLoading,
    resolvedState,
    selectedSharedViewLoading,
    selectedSharedViewQuery.isError,
    scopedPreferenceState,
    sharedViewsLoading,
    sharedViewsQuery.isError,
    shouldFetchSelectedSharedView,
    setIsApplyingRemotePreference,
  ])

  useEffect(() => {
    return () => {
      cancelApplyRelease()
    }
  }, [cancelApplyRelease])

  const captureCurrentGridViewPayload = useCallback(
    ({
      includeColumnSizing,
      externalFilters,
      filterModel,
    }: {
      includeColumnSizing?: boolean
      externalFilters?: ExternalFilters
      filterModel?: Record<string, unknown> | null
    } = {}) => {
      if (!api) return null

      const payload = captureGridViewPayload({
        api,
        externalFilters: externalFilters ?? externalFiltersSelectedRef.current,
        filterModel,
        contextualExternalFilterFields: config?.contextualExternalFilterFields,
        ignoreColumn: columnStateConfig?.ignoreColumn,
        includeColumnSizing,
      })

      if (
        !validateGridViewPayloadForApi({
          api,
          payload,
          ignoreColumn: columnStateConfig?.ignoreColumn,
        })
      ) {
        return null
      }

      return payload
    },
    [
      api,
      columnStateConfig?.ignoreColumn,
      config?.contextualExternalFilterFields,
      externalFiltersSelectedRef,
    ],
  )

  const getCompatibleStoredPersonalDraftPayload = useCallback(() => {
    const currentPreference = preferenceRef.current
    const draftPayload = normalizePreferenceDraftPayload(
      currentPreference?.draftPayload,
    )
    if (!draftPayload) return null
    if (
      !isCompatibleGridViewPayloadVersion(currentPreference?.payloadVersion)
    ) {
      return null
    }

    const schemaDrifted =
      currentPreference?.gridSchemaVersion !== config?.gridSchemaVersion
    const parsedPayload = parseGridViewPayload(draftPayload, {
      contextualExternalFilterFields: config?.contextualExternalFilterFields,
      rejectMalformed: schemaDrifted,
    })
    if (!parsedPayload) return null
    if (!schemaDrifted) {
      return draftPayload
    }
    if (!systemPayload) return null

    const reconciledPayload = reconcileGridViewPayload({
      payload: parsedPayload,
      systemPayload,
      contextualExternalFilterFields: config?.contextualExternalFilterFields,
      externalFilterFields,
    })
    return reconciledPayload
      ? serializeGridViewPayload(reconciledPayload, {
          contextualExternalFilterFields:
            config?.contextualExternalFilterFields,
        })
      : null
  }, [
    config?.contextualExternalFilterFields,
    config?.gridSchemaVersion,
    preferenceRef,
    systemPayload,
    externalFilterFields,
  ])

  const getCompatibleStoredLegacyPersonalDraftPayload = useCallback(() => {
    const currentPreference = preferenceRef.current
    const draftPayload = normalizePreferenceDraftPayload(
      currentPreference?.draftPayload,
    )
    if (!draftPayload) return null
    if (
      draftPayload ===
      normalizePreferenceDraftPayload(activeSelectedViewDraftPayloadRef.current)
    ) {
      return null
    }
    if (
      !isCompatibleGridViewPayloadVersion(currentPreference?.payloadVersion)
    ) {
      return null
    }

    const schemaDrifted =
      currentPreference?.gridSchemaVersion !== config?.gridSchemaVersion
    const parsedPayload = parseGridViewPayload(draftPayload, {
      contextualExternalFilterFields: config?.contextualExternalFilterFields,
      rejectMalformed: schemaDrifted,
    })
    if (!parsedPayload) return null
    if (!schemaDrifted) {
      return draftPayload
    }
    if (!systemPayload) return null

    const reconciledPayload = reconcileGridViewPayload({
      payload: parsedPayload,
      systemPayload,
      contextualExternalFilterFields: config?.contextualExternalFilterFields,
      externalFilterFields,
    })
    return reconciledPayload
      ? serializeGridViewPayload(reconciledPayload, {
          contextualExternalFilterFields:
            config?.contextualExternalFilterFields,
        })
      : null
  }, [
    config?.contextualExternalFilterFields,
    config?.gridSchemaVersion,
    preferenceRef,
    systemPayload,
    externalFilterFields,
  ])

  const parseCompatibleSharedViewPayload = useCallback(
    (view: GridViewItem | null | undefined) => {
      if (!view || !config) return null
      if (!isCompatibleGridViewPayloadVersion(view.payloadVersion)) return null

      const schemaDrifted = view.gridSchemaVersion !== config.gridSchemaVersion
      const parsedPayload = parseGridViewPayload(view.payload, {
        contextualExternalFilterFields: config.contextualExternalFilterFields,
        rejectMalformed: schemaDrifted,
      })
      if (!parsedPayload) return null
      if (!schemaDrifted) {
        return parsedPayload
      }

      return systemPayload
        ? reconcileGridViewPayload({
            payload: parsedPayload,
            systemPayload,
            contextualExternalFilterFields:
              config.contextualExternalFilterFields,
            externalFilterFields,
          })
        : null
    },
    [config, externalFilterFields, systemPayload],
  )

  const getPreservedPersonalDraftPayload = useCallback(
    () =>
      scopedInactivePersonalDraftPayload ??
      getCompatibleStoredPersonalDraftPayload(),
    [
      getCompatibleStoredPersonalDraftPayload,
      scopedInactivePersonalDraftPayload,
    ],
  )

  const getPersonalDraftPayloadForSwitch = useCallback(() => {
    const currentPreference = preferenceRef.current
    const canCaptureActivePersonalDraft =
      selectedRefCanCaptureActivePersonalDraft(
        currentPreference?.selectedViewRef,
      )
    const shouldPreserveDraft =
      (canCaptureActivePersonalDraft &&
        resolvedStateRef.current?.source === 'personal-draft' &&
        resolvedStateRef.current.currentKind === 'personal') ||
      (canCaptureActivePersonalDraft &&
        (hasPendingUserChange || userDraftSyncPendingRef.current))
    if (!shouldPreserveDraft) {
      const storedDraftPayload =
        getPreservedPersonalDraftPayload() ??
        getCompatibleStoredLegacyPersonalDraftPayload()
      if (storedDraftPayload) {
        setInactivePersonalDraftPayload(storedDraftPayload)
      }
      return storedDraftPayload
    }

    const currentPayload = captureCurrentGridViewPayload({
      includeColumnSizing:
        columnSizingDirtyRef.current ||
        hasColumnSizing(resolvedStateRef.current?.payload),
    })
    const nextDraftPayload = currentPayload
      ? serializeGridViewPayload(currentPayload, {
          contextualExternalFilterFields:
            config?.contextualExternalFilterFields,
        })
      : getPreservedPersonalDraftPayload()
    if (!nextDraftPayload) return

    setInactivePersonalDraftPayload(nextDraftPayload)
    return nextDraftPayload
  }, [
    captureCurrentGridViewPayload,
    config?.contextualExternalFilterFields,
    getCompatibleStoredLegacyPersonalDraftPayload,
    getPreservedPersonalDraftPayload,
    hasPendingUserChange,
    preferenceRef,
    resolvedStateRef,
  ])

  const getPersonalDraftPayloadForTarget = useCallback(
    (targetPayload: GridViewPayload) => {
      const draftPayload = getPersonalDraftPayloadForSwitch()
      if (!draftPayload) return null

      const parsedDraftPayload = parseGridViewPayload(draftPayload, {
        contextualExternalFilterFields: config?.contextualExternalFilterFields,
      })
      if (
        parsedDraftPayload &&
        areGridViewPayloadsEqual(parsedDraftPayload, targetPayload)
      ) {
        setInactivePersonalDraftPayload(null)
        return null
      }

      return draftPayload
    },
    [config?.contextualExternalFilterFields, getPersonalDraftPayloadForSwitch],
  )

  const syncDraftFromGrid = useCallback(
    async (capture?: GridViewChangeCapture) => {
      if (
        !enabled ||
        !config ||
        !api ||
        !resolvedStateRef.current ||
        !basePermissions.canReadSharedViews ||
        !preferenceCoordinator ||
        !userDraftSyncPendingRef.current
      ) {
        return
      }

      if (!canPerformViewAction()) return

      if (applyInProgressRef.current) {
        retryDraftSyncRef.current?.()
        return
      }

      const currentResolvedState = resolvedStateRef.current
      const currentSystemPayload = systemPayloadRef.current

      const activeSharedView =
        sharedViewsRef.current.find(
          (view) => view.id === currentResolvedState.activeSharedView?.id,
        ) ?? null
      const activeSharedPayload = activeSharedView
        ? parseCompatibleSharedViewPayload(activeSharedView)
        : null
      const currentSelectedRef = parseGridViewSelectedRef(
        preferenceRef.current?.selectedViewRef,
      )
      const activeSelectedViewRef: GridViewSelectedRef =
        currentResolvedState.currentKind === 'personal'
          ? PERSONAL_GRID_VIEW_REF
          : activeSharedView
            ? buildTeamGridViewRef(activeSharedView.id)
            : currentResolvedState.activeSystemView
              ? currentSelectedRef.type === 'system' &&
                currentSelectedRef.key ===
                  currentResolvedState.activeSystemView.key &&
                currentSelectedRef.isExplicitSelection
                ? buildExplicitSystemGridViewRef(
                    currentResolvedState.activeSystemView.key,
                  )
                : buildSystemGridViewRef(
                    currentResolvedState.activeSystemView.key,
                  )
              : DEFAULT_GRID_VIEW_REF
      const baselinePayload =
        activeSharedPayload ??
        currentResolvedState.activeSystemView?.payload ??
        currentSystemPayload
      if (!baselinePayload) return

      const currentPayload = captureCurrentGridViewPayload({
        externalFilters: capture?.externalFilters,
        filterModel: capture?.filterModel,
        includeColumnSizing:
          columnSizingDirtyRef.current || hasColumnSizing(baselinePayload),
      })
      if (!currentPayload) {
        retryDraftSyncRef.current?.()
        return
      }

      if (
        currentResolvedState.source === 'personal-draft' &&
        areGridViewPayloadsEqual(currentPayload, currentResolvedState.payload)
      ) {
        userDraftSyncPendingRef.current = false
        setHasPendingUserChange(false)
        return
      }

      const shouldPersistDraft = !areGridViewPayloadsEqual(
        currentPayload,
        baselinePayload,
      )
      const preservedPersonalDraftPayload = getPreservedPersonalDraftPayload()
      const hasInactivePersonalDraft = Boolean(
        preservedPersonalDraftPayload &&
        (scopedInactivePersonalDraftPayload ||
          selectedRefHasInactivePersonalDraft(
            preferenceRef.current?.selectedViewRef,
          )),
      )

      if (!shouldPersistDraft) {
        const currentPreference = preferenceRef.current
        const nextDraftPayload =
          currentResolvedState.source === 'personal-draft'
            ? null
            : preservedPersonalDraftPayload
        const baseSelectedViewRef =
          !nextDraftPayload && activeSelectedViewRef === PERSONAL_GRID_VIEW_REF
            ? DEFAULT_GRID_VIEW_REF
            : activeSelectedViewRef
        const nextSelectedViewRef = parkPersonalDraftOnSelectedRef({
          selectedViewRef: baseSelectedViewRef,
          draftPayload: nextDraftPayload,
        })
        if (
          normalizePreferenceDraftPayload(currentPreference?.draftPayload) ===
            normalizePreferenceDraftPayload(nextDraftPayload) &&
          (currentPreference?.selectedViewRef ?? DEFAULT_GRID_VIEW_REF) ===
            nextSelectedViewRef
        ) {
          userDraftSyncPendingRef.current = false
          setHasPendingUserChange(false)
          return
        }

        userDraftSyncPendingRef.current = false
        setHasPendingUserChange(false)
        activeSelectedViewDraftPayloadRef.current = null
        await persistPreference({
          selectedViewRef: nextSelectedViewRef,
          draftPayload: nextDraftPayload,
          flush: false,
        })
        return
      }

      if (
        hasInactivePersonalDraft &&
        currentResolvedState.currentKind !== 'personal'
      ) {
        const serializedCurrentPayload = serializeGridViewPayload(
          currentPayload,
          {
            contextualExternalFilterFields:
              config.contextualExternalFilterFields,
          },
        )
        userDraftSyncPendingRef.current = false
        setHasPendingUserChange(true)
        setInactivePersonalDraftPayload(serializedCurrentPayload)
        await persistPreference({
          selectedViewRef: parkPersonalDraftOnSelectedRef({
            selectedViewRef: activeSelectedViewRef,
            draftPayload: serializedCurrentPayload,
          }),
          draftPayload: serializedCurrentPayload,
          flush: false,
        })
        return
      }

      if (
        lastAppliedPayloadRef.current &&
        areGridViewPayloadsEqual(currentPayload, lastAppliedPayloadRef.current)
      ) {
        userDraftSyncPendingRef.current = false
        setHasPendingUserChange(false)
        return
      }

      userDraftSyncPendingRef.current = false
      setHasPendingUserChange(false)
      setInactivePersonalDraftPayload(null)
      const serializedCurrentPayload = serializeGridViewPayload(
        currentPayload,
        {
          contextualExternalFilterFields: config.contextualExternalFilterFields,
        },
      )
      activeSelectedViewDraftPayloadRef.current =
        currentResolvedState.currentKind === 'personal'
          ? null
          : serializedCurrentPayload
      await persistPreference({
        selectedViewRef: activeSelectedViewRef,
        draftPayload: serializedCurrentPayload,
        flush: false,
      })
    },
    [
      api,
      basePermissions.canReadSharedViews,
      captureCurrentGridViewPayload,
      canPerformViewAction,
      config,
      enabled,
      getPreservedPersonalDraftPayload,
      persistPreference,
      preferenceCoordinator,
      preferenceCoordinatorKey,
      preferenceRef,
      resolvedStateRef,
      sharedViewsRef,
      scopedInactivePersonalDraftPayload,
      systemPayloadRef,
      hydratedPreferenceCoordinatorKey,
      parseCompatibleSharedViewPayload,
    ],
  )

  const syncDraftFromGridRef = useLatestRef(syncDraftFromGrid)

  useEffect(() => {
    if (!preferenceCoordinatorKey) {
      setPreferenceCoordinatorEntry(null)
      setHydratedPreferenceCoordinatorKey(null)
      return
    }

    const coordinator = getGridViewPreferenceCoordinator({
      key: preferenceCoordinatorKey,
      writer: preferenceWriterRef.current,
    })
    setPreferenceCoordinatorEntry((current) =>
      current?.key === preferenceCoordinatorKey &&
      current.coordinator === coordinator
        ? current
        : { key: preferenceCoordinatorKey, coordinator },
    )

    return () => {
      const timer = draftSyncTimerRef.current
      if (timer) {
        clearTimeout(timer)
        draftSyncTimerRef.current = null
      }

      const capture = userDraftSyncPendingRef.current
        ? syncDraftFromGridRef.current()
        : Promise.resolve()
      void capture
        .catch(() => undefined)
        .then(() => coordinator.flush())
        .catch(() => undefined)
        .finally(() => coordinator.release())

      setPreferenceCoordinatorEntry((current) =>
        current?.coordinator === coordinator ? null : current,
      )
    }
  }, [preferenceCoordinatorKey, preferenceWriterRef, syncDraftFromGridRef])

  const scheduleDraftRetry = useCallback(() => {
    if (draftSyncRetryCountRef.current >= 3 || draftSyncTimerRef.current) {
      return
    }

    draftSyncRetryCountRef.current += 1
    draftSyncTimerRef.current = setTimeout(() => {
      draftSyncTimerRef.current = null
      void syncDraftFromGridRef.current()
    }, GRID_VIEW_PREFERENCE_DEBOUNCE_MS)
  }, [syncDraftFromGridRef])

  useEffect(() => {
    retryDraftSyncRef.current = scheduleDraftRetry
    return () => {
      if (retryDraftSyncRef.current === scheduleDraftRetry) {
        retryDraftSyncRef.current = null
      }
    }
  }, [scheduleDraftRetry])

  const markUserGridViewChange = useCallback(
    (capture?: GridViewChangeCapture) => {
      draftSyncRetryCountRef.current = 0
      setHasPendingUserChange(true)
      userDraftSyncPendingRef.current = true
      void syncDraftFromGridRef.current(capture)
    },
    [syncDraftFromGridRef],
  )

  useEffect(() => {
    if (
      !preferenceCoordinator ||
      !serverQueriesReady ||
      hydratedPreferenceCoordinatorKey !== preferenceCoordinatorKey ||
      !userDraftSyncPendingRef.current
    ) {
      return
    }

    void syncDraftFromGridRef.current()
  }, [
    hydratedPreferenceCoordinatorKey,
    preferenceCoordinator,
    preferenceCoordinatorKey,
    serverQueriesReady,
    syncDraftFromGridRef,
  ])

  const notifyUserGridViewChange = useCallback(
    (capture?: GridViewChangeCapture) => {
      if (!enabled || applyInProgressRef.current || !canPerformViewAction()) {
        return
      }

      markUserGridViewChange(capture)
    },
    [canPerformViewAction, enabled, markUserGridViewChange],
  )

  useEffect(() => {
    if (!enabled || !api || !gridIsReady) return

    const handleStructuralViewChange = (
      eventName: string,
      event?:
        | { finished?: boolean; source?: unknown }
        | { type?: string; source?: unknown }
        | undefined,
    ) => {
      if (event && 'finished' in event && event.finished === false) {
        return
      }
      if (!isUserGridViewEvent(event) || !canPerformViewAction()) return

      if (eventName === 'columnResized') {
        columnSizingDirtyRef.current = true
      }
      markUserGridViewChange()
    }

    const events: Array<
      | 'columnVisible'
      | 'columnMoved'
      | 'columnResized'
      | 'columnPinned'
      | 'sortChanged'
      | 'filterChanged'
    > = [
      'columnVisible',
      'columnMoved',
      'columnResized',
      'columnPinned',
      'sortChanged',
      'filterChanged',
    ]

    const listeners = events.map((eventName) => {
      const listener = (event?: { finished?: boolean; source?: unknown }) =>
        handleStructuralViewChange(eventName, event)
      api.addEventListener(eventName, listener)
      return { eventName, listener }
    })

    return () => {
      if (api.isDestroyed?.()) return
      listeners.forEach(({ eventName, listener }) => {
        try {
          api.removeEventListener(eventName, listener)
        } catch {
          // ignore grid teardown races
        }
      })
    }
  }, [api, canPerformViewAction, enabled, gridIsReady, markUserGridViewChange])

  const upsertLocalView = useCallback((nextView: GridViewItem) => {
    setSharedViewsState((currentViews) =>
      [
        ...currentViews.filter((view) => view.id !== nextView.id),
        nextView,
      ].sort(sortViewsByUpdatedAtDesc),
    )
  }, [])

  const applyPersonalView = useCallback(async () => {
    if (
      !canPerformViewAction() ||
      !api ||
      !basePermissions.canReadSharedViews
    ) {
      return
    }

    const draftPayload = getPreservedPersonalDraftPayload()
    const payload = parseGridViewPayload(draftPayload, {
      contextualExternalFilterFields: config?.contextualExternalFilterFields,
    })
    if (!payload) return

    applyResolvedPayload(payload)
    setInactivePersonalDraftPayload(null)
    await persistPreference({
      selectedViewRef: PERSONAL_GRID_VIEW_REF,
      draftPayload: serializeGridViewPayload(payload, {
        contextualExternalFilterFields: config?.contextualExternalFilterFields,
      }),
      force: true,
    })
  }, [
    api,
    applyResolvedPayload,
    basePermissions.canReadSharedViews,
    canPerformViewAction,
    config?.contextualExternalFilterFields,
    getPreservedPersonalDraftPayload,
    persistPreference,
  ])

  const applySystemDefaultView = useCallback(async () => {
    if (!canPerformViewAction()) return

    const defaultSystemView = systemViews.find((view) => view.isDefault) ?? null
    const payload = config?.preferDefinitionFallback
      ? systemPayload
      : (defaultSystemView?.payload ?? systemPayload)
    if (!payload) return
    const selectedViewRef: GridViewSelectedRef =
      config?.preferDefinitionFallback || !defaultSystemView
        ? DEFAULT_GRID_VIEW_REF
        : buildSystemGridViewRef(defaultSystemView.key)
    const draftPayload = getPersonalDraftPayloadForTarget(payload)
    applyResolvedPayload(payload)
    await persistPreference({
      selectedViewRef: parkPersonalDraftOnSelectedRef({
        selectedViewRef,
        draftPayload,
      }),
      draftPayload,
    })
  }, [
    applyResolvedPayload,
    canPerformViewAction,
    config?.preferDefinitionFallback,
    getPersonalDraftPayloadForTarget,
    persistPreference,
    systemPayload,
    systemViews,
  ])

  const applySystemView = useCallback(
    async (viewKey: string) => {
      if (!canPerformViewAction()) return

      const systemView = systemViews.find((view) => view.key === viewKey)
      if (!systemView) return

      const payload = systemView.payload
      const draftPayload = getPersonalDraftPayloadForTarget(payload)
      applyResolvedPayload(payload)
      await persistPreference({
        selectedViewRef: parkPersonalDraftOnSelectedRef({
          selectedViewRef: buildExplicitSystemGridViewRef(systemView.key),
          draftPayload,
        }),
        draftPayload,
      })
    },
    [
      applyResolvedPayload,
      canPerformViewAction,
      getPersonalDraftPayloadForTarget,
      persistPreference,
      systemViews,
    ],
  )

  const applySharedView = useCallback(
    async (viewId: string) => {
      if (!canPerformViewAction()) return

      const view = sharedViewsRef.current.find((item) => item.id === viewId)
      if (!view || !basePermissions.canReadSharedViews) return

      const payload = parseCompatibleSharedViewPayload(view)
      if (!payload) return

      const draftPayload = getPersonalDraftPayloadForTarget(payload)
      applyResolvedPayload(payload)
      await persistPreference({
        selectedViewRef: parkPersonalDraftOnSelectedRef({
          selectedViewRef: buildTeamGridViewRef(view.id),
          draftPayload,
        }),
        draftPayload,
      })
    },
    [
      applyResolvedPayload,
      basePermissions.canReadSharedViews,
      canPerformViewAction,
      getPersonalDraftPayloadForTarget,
      parseCompatibleSharedViewPayload,
      persistPreference,
      sharedViewsRef,
    ],
  )

  const restoreSavedView = useCallback(async () => {
    if (!canPerformViewAction()) return

    const currentSharedView =
      sharedViewsRef.current.find(
        (view) => view.id === resolvedState?.activeSharedView?.id,
      ) ?? null
    if (!currentSharedView) return

    const payload = parseCompatibleSharedViewPayload(currentSharedView)
    if (!payload) return

    const draftPayload = getPersonalDraftPayloadForTarget(payload)
    applyResolvedPayload(payload)
    await persistPreference({
      selectedViewRef: parkPersonalDraftOnSelectedRef({
        selectedViewRef: buildTeamGridViewRef(currentSharedView.id),
        draftPayload,
      }),
      draftPayload,
    })
  }, [
    applyResolvedPayload,
    canPerformViewAction,
    getPersonalDraftPayloadForTarget,
    parseCompatibleSharedViewPayload,
    persistPreference,
    resolvedState,
    sharedViewsRef,
  ])

  const saveAsSharedView = useCallback(
    async ({
      name,
      description,
      setAsTeamDefault,
    }: {
      name: string
      description?: string
      setAsTeamDefault?: boolean
    }) => {
      assertViewActionAllowed()
      if (
        !config ||
        !canonicalScopeKey ||
        !basePermissions.canCreateSharedView ||
        !api ||
        !userId
      ) {
        return
      }

      const payload = captureCurrentGridViewPayload({
        includeColumnSizing:
          columnSizingDirtyRef.current ||
          hasColumnSizing(resolvedStateRef.current?.payload),
      })
      if (!payload) return

      const id = uuidv4()
      const timestamp = getNextTimestamp()
      const nextView: GridViewItem = {
        id,
        organizationID: activeOrganizationId!,
        scopeKey: canonicalScopeKey,
        name,
        description: description || null,
        payload: serializeGridViewPayload(payload),
        payloadVersion: getGridViewPayloadVersion(),
        gridSchemaVersion: config.gridSchemaVersion,
        status: 'active',
        isDefault:
          Boolean(setAsTeamDefault) && basePermissions.canSetDefaultSharedView,
        createdByUserID: userId,
        updatedByUserID: userId,
        createdAt: timestamp,
        updatedAt: timestamp,
        source: 'manage',
      }

      const previousDefaults =
        nextView.isDefault && basePermissions.canSetDefaultSharedView
          ? sharedViewsRef.current.filter((view) => view.isDefault)
          : []
      await createGridViewMutation.mutateAsync(nextView as GridViewInput)
      const clearedDefaults: GridViewItem[] = []

      for (const defaultView of previousDefaults) {
        const clearedDefault: GridViewItem = {
          ...defaultView,
          scopeKey: canonicalScopeKey,
          isDefault: false,
          updatedByUserID: userId,
          updatedAt: timestamp,
          source: 'manage',
        }
        await updateGridViewMutation.mutateAsync({
          input: clearedDefault as GridViewInput,
          currentView: defaultView,
        })
        clearedDefaults.push(clearedDefault)
      }

      upsertLocalView(nextView)
      clearedDefaults.forEach(upsertLocalView)

      setInactivePersonalDraftPayload(null)
      await persistPreference({
        selectedViewRef: buildTeamGridViewRef(nextView.id),
        draftPayload: null,
      })
    },
    [
      activeOrganizationId,
      assertViewActionAllowed,
      api,
      basePermissions.canCreateSharedView,
      basePermissions.canSetDefaultSharedView,
      canonicalScopeKey,
      canPerformViewAction,
      config,
      captureCurrentGridViewPayload,
      createGridViewMutation,
      persistPreference,
      resolvedStateRef,
      sharedViewsRef,
      updateGridViewMutation,
      upsertLocalView,
      userId,
    ],
  )

  const saveChangesToCurrentSharedView = useCallback(async () => {
    if (!canPerformViewAction()) return

    const currentSharedView =
      sharedViewsRef.current.find(
        (view) => view.id === resolvedState?.activeSharedView?.id,
      ) ?? null
    if (!currentSharedView || !api) return
    if (!permissionsForView(currentSharedView).canUpdateSharedView) return

    const payload = captureCurrentGridViewPayload({
      includeColumnSizing:
        columnSizingDirtyRef.current || hasColumnSizing(resolvedState?.payload),
    })
    if (!payload) return

    const nextView: GridViewItem = {
      ...currentSharedView,
      scopeKey: canonicalScopeKey ?? currentSharedView.scopeKey,
      payload: serializeGridViewPayload(payload),
      payloadVersion: getGridViewPayloadVersion(),
      gridSchemaVersion:
        config?.gridSchemaVersion ?? currentSharedView.gridSchemaVersion,
      updatedByUserID: userId ?? currentSharedView.updatedByUserID,
      updatedAt: getNextTimestamp(),
      source: 'manage',
    }

    await updateGridViewMutation.mutateAsync({
      input: nextView as GridViewInput,
      currentView: currentSharedView,
    })
    upsertLocalView(nextView)
    setInactivePersonalDraftPayload(null)
    await persistPreference({
      selectedViewRef: buildTeamGridViewRef(nextView.id),
      draftPayload: null,
    })
  }, [
    api,
    canonicalScopeKey,
    canPerformViewAction,
    config?.gridSchemaVersion,
    captureCurrentGridViewPayload,
    permissionsForView,
    persistPreference,
    resolvedState,
    sharedViewsRef,
    updateGridViewMutation,
    upsertLocalView,
    userId,
  ])

  const updateSharedViewMeta = useCallback(
    async ({
      viewId,
      name,
      description,
    }: {
      viewId: string
      name: string
      description?: string
    }) => {
      assertViewActionAllowed()

      const currentView = sharedViewsRef.current.find(
        (view) => view.id === viewId,
      )
      if (!currentView) return
      if (!permissionsForView(currentView).canUpdateSharedView) return

      const nextView: GridViewItem = {
        ...currentView,
        scopeKey: canonicalScopeKey ?? currentView.scopeKey,
        name,
        description: description || null,
        updatedByUserID: userId ?? currentView.updatedByUserID,
        updatedAt: getNextTimestamp(),
        source: 'manage',
      }

      await updateGridViewMutation.mutateAsync({
        input: nextView as GridViewInput,
        currentView,
      })
      upsertLocalView(nextView)
    },
    [
      assertViewActionAllowed,
      canonicalScopeKey,
      permissionsForView,
      sharedViewsRef,
      updateGridViewMutation,
      upsertLocalView,
      userId,
    ],
  )

  const deleteSharedView = useCallback(
    async (viewId: string) => {
      assertViewActionAllowed()

      const currentView = sharedViewsRef.current.find(
        (view) => view.id === viewId,
      )
      if (!currentView || !userId) return
      if (!permissionsForView(currentView).canDeleteSharedView) return

      const selectedPreference = preferenceRef.current

      await deleteGridViewMutation.mutateAsync({
        input: {
          id: viewId,
          organizationID: currentView.organizationID,
          scopeKey: canonicalScopeKey ?? currentView.scopeKey,
          updatedByUserID: userId,
        },
        currentView,
      })

      setSharedViewsState((currentViews) =>
        currentViews.filter((view) => view.id !== viewId),
      )
      if (selectedSharedViewDetailRef.current?.view.id === viewId) {
        setSelectedSharedViewDetail(null)
      }

      const selectedRef = parseGridViewSelectedRef(
        selectedPreference?.selectedViewRef,
      )
      if (selectedRef.type === 'team' && selectedRef.id === viewId) {
        const draftPayload = selectedPreference?.draftPayload ?? null
        await persistPreference({
          selectedViewRef: parkPersonalDraftOnSelectedRef({
            selectedViewRef: DEFAULT_GRID_VIEW_REF,
            draftPayload,
          }),
          draftPayload,
        })
      }
    },
    [
      assertViewActionAllowed,
      canonicalScopeKey,
      deleteGridViewMutation,
      permissionsForView,
      persistPreference,
      preferenceRef,
      sharedViewsRef,
      userId,
    ],
  )

  const setDefaultSharedView = useCallback(
    async (viewId: string) => {
      if (!canPerformViewAction() || !basePermissions.canSetDefaultSharedView) {
        return
      }

      const targetView = sharedViewsRef.current.find(
        (view) => view.id === viewId,
      )
      if (!targetView) return

      const previousDefaults = sharedViewsRef.current.filter(
        (view) => view.id !== viewId && view.isDefault,
      )
      const timestamp = getNextTimestamp()

      const nextTarget: GridViewItem = {
        ...targetView,
        scopeKey: canonicalScopeKey ?? targetView.scopeKey,
        isDefault: true,
        updatedByUserID: userId ?? targetView.updatedByUserID,
        updatedAt: timestamp,
        source: 'manage',
      }

      await updateGridViewMutation.mutateAsync({
        input: nextTarget as GridViewInput,
        currentView: targetView,
      })
      const clearedDefaults: GridViewItem[] = []

      for (const previousDefault of previousDefaults) {
        const clearedDefault: GridViewItem = {
          ...previousDefault,
          scopeKey: canonicalScopeKey ?? previousDefault.scopeKey,
          isDefault: false,
          updatedByUserID: userId ?? previousDefault.updatedByUserID,
          updatedAt: timestamp,
          source: 'manage',
        }
        await updateGridViewMutation.mutateAsync({
          input: clearedDefault as GridViewInput,
          currentView: previousDefault,
        })
        clearedDefaults.push(clearedDefault)
      }

      upsertLocalView(nextTarget)
      clearedDefaults.forEach(upsertLocalView)
    },
    [
      basePermissions.canSetDefaultSharedView,
      canonicalScopeKey,
      canPerformViewAction,
      sharedViewsRef,
      updateGridViewMutation,
      upsertLocalView,
      userId,
    ],
  )

  const retryPreferenceWrite = useCallback(async () => {
    if (!canPerformViewAction()) return
    await preferenceCoordinator?.retry()
  }, [canPerformViewAction, preferenceCoordinator])

  const retrySharedViews = useCallback(async () => {
    if (!enabled || !basePermissions.canReadSharedViews) return
    await Promise.all([
      sharedViewsQuery.refetch(),
      canonicalPreferenceQuery.refetch(),
      ...(shouldFetchSelectedSharedView
        ? [selectedSharedViewQuery.refetch()]
        : []),
    ])
  }, [
    basePermissions.canReadSharedViews,
    canonicalPreferenceQuery.refetch,
    enabled,
    selectedSharedViewQuery.refetch,
    sharedViewsQuery.refetch,
    shouldFetchSelectedSharedView,
  ])

  const resolvePreferenceConflict = useCallback(
    async (choice: 'local' | 'remote') => {
      const resolutionCoordinator = preferenceCoordinator
      const resolutionScopeKey = viewStateScopeKey
      const resolutionScopeEpoch = viewStateScopeEpoch
      const isResolutionScopeCurrent = () =>
        viewStateScopeEpochRef.current.key === resolutionScopeKey &&
        viewStateScopeEpochRef.current.epoch === resolutionScopeEpoch

      if (
        serverQueriesError ||
        isImpersonating ||
        !resolutionCoordinator ||
        conflictResolutionPromiseRef.current ||
        !isResolutionScopeCurrent()
      ) {
        return
      }

      const resolutionPromise = (async () => {
        let remoteApplyPending = false
        try {
          const refreshed = await canonicalPreferenceQuery.refetch()
          if (
            !isResolutionScopeCurrent() ||
            preferenceWriteBlockedRef.current ||
            refreshed.isError ||
            !resolutionCoordinator.getConflict()
          ) {
            return
          }
          const remotePreference = refreshed.data ?? null
          if (choice === 'remote') {
            setIsApplyingRemotePreference(true)
          }
          await resolutionCoordinator.resolveConflict(
            choice,
            remotePreference?.revision ?? 0,
          )
          if (!isResolutionScopeCurrent()) return
          if (
            choice === 'remote' &&
            resolutionCoordinator.getState() === 'idle' &&
            !resolutionCoordinator.getPendingSnapshot()
          ) {
            remotePreferenceApplyRef.current = remotePreference
            setPreferenceState(remotePreference)
            remoteApplyPending = true
          }
        } finally {
          if (
            choice === 'remote' &&
            !remoteApplyPending &&
            isResolutionScopeCurrent()
          ) {
            setIsApplyingRemotePreference(false)
          }
        }
      })()
      conflictResolutionPromiseRef.current = resolutionPromise

      try {
        await resolutionPromise
      } finally {
        if (conflictResolutionPromiseRef.current === resolutionPromise) {
          conflictResolutionPromiseRef.current = null
        }
      }
    },
    [
      canonicalPreferenceQuery.refetch,
      isImpersonating,
      preferenceCoordinator,
      serverQueriesError,
      viewStateScopeEpoch,
      viewStateScopeEpochRef,
      viewStateScopeKey,
    ],
  )

  const pendingPreferenceSnapshot =
    preferenceCoordinator?.getPendingSnapshot() ?? null
  const acknowledgedPreferenceSnapshot =
    preferenceCoordinator?.getLastAcknowledgedSnapshot() ?? null
  const hasStaleAcknowledgedSnapshot = Boolean(
    acknowledgedPreferenceSnapshot &&
    typeof acknowledgedPreferenceSnapshot.expectedRevision === 'number' &&
    (preferenceData?.revision ?? 0) <
      acknowledgedPreferenceSnapshot.expectedRevision,
  )
  const localPreferenceSnapshot =
    pendingPreferenceSnapshot ??
    (hasStaleAcknowledgedSnapshot ? acknowledgedPreferenceSnapshot : null)
  const pendingPreference =
    localPreferenceSnapshot && userId
      ? buildPendingPreferenceItem({
          snapshot: localPreferenceSnapshot,
          userId,
          currentPreference: scopedPreferenceState,
        })
      : null
  const preferenceConflict = preferenceCoordinator?.getConflict() ?? null
  const isPreferenceConflictReady = Boolean(
    preferenceConflict &&
    hydratedPreferenceCoordinatorKey === preferenceCoordinatorKey &&
    pendingPreference &&
    isSamePreferenceSnapshot(scopedPreferenceState, pendingPreference),
  )
  const isPreferenceBootstrapReady =
    !preferenceCoordinatorKey ||
    (Boolean(preferenceCoordinator) &&
      hydratedPreferenceCoordinatorKey === preferenceCoordinatorKey &&
      !preferenceConflict &&
      (!localPreferenceSnapshot ||
        Boolean(
          pendingPreference &&
          isSamePreferenceSnapshot(scopedPreferenceState, pendingPreference),
        )))
  const isPreferenceCoordinatorHydrated =
    !preferenceCoordinatorKey ||
    serverQueriesError ||
    hydratedPreferenceCoordinatorKey === preferenceCoordinatorKey

  const initialGridState = useMemo(
    () =>
      resolvedState
        ? buildGridViewInitialState(resolvedState.payload)
        : undefined,
    [resolvedState],
  )
  const isInitialStateReady = Boolean(
    resolvedState &&
    serverQueriesReady &&
    columnDefsReady &&
    (isPreferenceBootstrapReady ||
      isPreferenceConflictReady ||
      serverQueriesError),
  )
  const readState: GridViewsContextValue['readState'] =
    !basePermissions.canReadSharedViews
      ? 'ready'
      : serverQueriesError
        ? 'error'
        : !serverQueriesReady
          ? 'loading'
          : 'ready'
  const writeState: GridViewsContextValue['writeState'] =
    preferenceWriteState === 'conflict'
      ? 'conflict'
      : preferenceWriteState === 'error'
        ? 'error'
        : preferenceWriteState === 'pending' ||
            upsertPreferenceMutation.isPending
          ? 'pending'
          : 'idle'
  const isInteractionBlocked =
    Boolean(preferenceConflict) || isApplyingRemotePreference

  if (!enabled || !config || !resolvedState) {
    return enabled
      ? {
          enabled: true,
          isLoading: true,
          isInitialStateReady: false,
          initialStateSignature: null,
          initialExternalFilters: {},
          currentViewKind: 'system',
          currentSharedViewId: null,
          currentSharedViewName: null,
          currentSharedViewDescription: null,
          currentSharedViewCreatedByUserID: null,
          currentSharedViewIsDefault: false,
          currentSystemViewKey: null,
          currentSystemViewName: null,
          currentSystemViewDescription: null,
          isModified: false,
          hasPersonalDraft: false,
          hasPersonalView: false,
          hasInactivePersonalDraft: false,
          hasDefinitionDefault: false,
          viewSource: 'system',
          systemViews: [],
          sharedViews: [],
          canCreateSharedView: false,
          canEditSharedView: () => false,
          canDeleteSharedView: () => false,
          canSetDefaultSharedView: false,
          readState,
          writeState,
          isInteractionBlocked,
          retrySharedViews,
          retryPreferenceWrite,
          resolvePreferenceConflict,
          notifyUserGridViewChange: () => {},
          applyPersonalView: async () => {},
          applySystemDefaultView: async () => {},
          applySystemView: async () => {},
          applySharedView: async () => {},
          restoreSavedView: async () => {},
          saveAsSharedView: async () => {},
          saveChangesToCurrentSharedView: async () => {},
          updateSharedViewMeta: async () => {},
          deleteSharedView: async () => {},
          setDefaultSharedView: async () => {},
        }
      : null
  }

  const scopedPreferenceDraftPayload = normalizePreferenceDraftPayload(
    scopedPreferenceState?.draftPayload,
  )
  const scopedPreferencePersonalDraftPayload = scopedPreferenceDraftPayload
  const scopedPersonalDraftPayload =
    scopedPreferencePersonalDraftPayload ?? scopedInactivePersonalDraftPayload
  const isScopedPreferenceDraftPayload =
    Boolean(scopedPreferencePersonalDraftPayload) &&
    scopedPersonalDraftPayload === scopedPreferencePersonalDraftPayload
  const hasActivePersonalDraft =
    resolvedState.source === 'personal-draft' &&
    resolvedState.currentKind === 'personal'
  const personalDraftSchemaDrifted =
    scopedPreferenceState?.gridSchemaVersion !== config.gridSchemaVersion
  const parsedPersonalDraftPayload = scopedPersonalDraftPayload
    ? parseGridViewPayload(scopedPersonalDraftPayload, {
        contextualExternalFilterFields: config.contextualExternalFilterFields,
        rejectMalformed: personalDraftSchemaDrifted,
      })
    : null
  const hasCompatiblePersonalDraft =
    !isScopedPreferenceDraftPayload ||
    (Boolean(parsedPersonalDraftPayload) &&
      isCompatibleGridViewPayloadVersion(
        scopedPreferenceState?.payloadVersion,
      ) &&
      (scopedPreferenceState?.gridSchemaVersion === config.gridSchemaVersion ||
        Boolean(
          systemPayload &&
          reconcileGridViewPayload({
            payload: parsedPersonalDraftPayload!,
            systemPayload,
            contextualExternalFilterFields:
              config.contextualExternalFilterFields,
            externalFilterFields,
          }),
        )))
  const hasPersonalView = Boolean(
    scopedPersonalDraftPayload &&
    parsedPersonalDraftPayload &&
    hasCompatiblePersonalDraft,
  )

  return {
    enabled: true,
    isLoading:
      sharedViewsLoading ||
      preferencesLoading ||
      selectedSharedViewLoading ||
      !isPreferenceCoordinatorHydrated ||
      createGridViewMutation.isPending ||
      updateGridViewMutation.isPending ||
      deleteGridViewMutation.isPending ||
      upsertPreferenceMutation.isPending,
    isInitialStateReady,
    initialStateSignature: isInitialStateReady ? resolvedState.signature : null,
    initialGridState: isInitialStateReady ? initialGridState : undefined,
    initialGridViewPayload: isInitialStateReady
      ? resolvedState.payload
      : undefined,
    initialExternalFilters: isInitialStateReady
      ? resolvedState.payload.externalFilters
      : {},
    currentViewKind: resolvedState.currentKind,
    currentSharedViewId: resolvedState.activeSharedView?.id ?? null,
    currentSharedViewName: resolvedState.activeSharedView?.name ?? null,
    currentSharedViewDescription:
      resolvedState.activeSharedView?.description ?? null,
    currentSharedViewCreatedByUserID:
      resolvedState.activeSharedView?.createdByUserID ?? null,
    currentSharedViewIsDefault:
      resolvedState.activeSharedView?.isDefault ?? false,
    currentSystemViewKey: resolvedState.activeSystemView?.key ?? null,
    currentSystemViewName: resolvedState.activeSystemView?.name ?? null,
    currentSystemViewDescription:
      resolvedState.activeSystemView?.description ?? null,
    isModified: resolvedState.isModified || hasPendingUserChange,
    hasPersonalDraft: hasActivePersonalDraft,
    hasPersonalView,
    hasInactivePersonalDraft: hasPersonalView && !hasActivePersonalDraft,
    hasDefinitionDefault: Boolean(config?.preferDefinitionFallback),
    viewSource: resolvedState.source,
    systemViews: systemViews.map((view) => ({
      key: view.key,
      name: view.name,
      description: view.description,
      isDefault: view.isDefault,
      isCompatible: view.isCompatible,
    })),
    sharedViews: effectiveSharedViewsState.map((view) => ({
      id: view.id,
      name: view.name,
      description: view.description,
      createdByUserID: view.createdByUserID,
      isDefault: view.isDefault,
      isCompatible: Boolean(parseCompatibleSharedViewPayload(view)),
    })),
    canCreateSharedView:
      !isImpersonating && basePermissions.canCreateSharedView,
    canEditSharedView: (viewId) =>
      !isImpersonating &&
      permissionsForView(
        effectiveSharedViewsState.find((view) => view.id === viewId),
      ).canUpdateSharedView,
    canDeleteSharedView: (viewId) =>
      !isImpersonating &&
      permissionsForView(
        effectiveSharedViewsState.find((view) => view.id === viewId),
      ).canDeleteSharedView,
    canSetDefaultSharedView:
      !isImpersonating && basePermissions.canSetDefaultSharedView,
    readState,
    writeState,
    isInteractionBlocked,
    retrySharedViews,
    retryPreferenceWrite,
    resolvePreferenceConflict,
    notifyUserGridViewChange,
    applyPersonalView,
    applySystemDefaultView,
    applySystemView,
    applySharedView,
    restoreSavedView,
    saveAsSharedView,
    saveChangesToCurrentSharedView,
    updateSharedViewMeta,
    deleteSharedView,
    setDefaultSharedView,
  }
}
